/**
 * CustomerStats Component
 *
 * Displays summary cards for open support tickets, recent invoices, account status,
 * total spent, and last login.
 * Supports loading state with skeleton placeholders.
 *
 * Requirements: 7.1, 7.2 - Display summary cards for customer dashboard
 */

import { Card, CardContent, CardHeader, CardTitle } from '@bloomcode/ui';
import { Ticket, FileText, CheckCircle, AlertCircle, CreditCard, Clock } from '@bloomcode/icons';

export interface CustomerStatsData {
  openTickets: number;
  recentInvoices: number;
  accountStatus: string;
  pendingPayments?: number;
  totalSpent?: number | string;
  lastLogin?: string;
}

interface CustomerStatsProps {
  stats: CustomerStatsData | null;
  isLoading?: boolean;
}

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  description?: string;
  isLoading?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

function StatCardSkeleton() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="h-4 w-4 animate-pulse rounded bg-muted" />
      </CardHeader>
      <CardContent>
        <div className="h-8 w-16 animate-pulse rounded bg-muted" />
        <div className="mt-1 h-3 w-32 animate-pulse rounded bg-muted" />
      </CardContent>
    </Card>
  );
}

const variantStyles = {
  default: 'text-muted-foreground',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
};

function StatCard({
  title,
  value,
  icon,
  description,
  isLoading,
  variant = 'default',
}: StatCardProps) {
  if (isLoading) {
    return <StatCardSkeleton />;
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className={variantStyles[variant]}>{icon}</div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </CardContent>
    </Card>
  );
}

function getAccountStatusDisplay(status: string) {
  const normalized = status?.toLowerCase();
  switch (normalized) {
    case 'active':
      return {
        label: 'Active',
        variant: 'success' as const,
        description: 'Your account is in good standing',
      };
    case 'pending':
      return {
        label: 'Pending',
        variant: 'warning' as const,
        description: 'Account verification in progress',
      };
    case 'suspended':
      return {
        label: 'Suspended',
        variant: 'danger' as const,
        description: 'Please contact support',
      };
    case 'inactive':
      return {
        label: 'Inactive',
        variant: 'danger' as const,
        description: 'Account is inactive',
      };
    default:
      return { label: 'Unknown', variant: 'default' as const, description: '' };
  }
}

function formatCurrency(amount: number | string): string {
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(numAmount);
}

function formatRelativeDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown';
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function CustomerStats({ stats, isLoading }: CustomerStatsProps) {
  const accountStatusDisplay = stats ? getAccountStatusDisplay(stats.accountStatus) : null;

  const statCards: StatCardProps[] = [
    {
      title: 'Open Tickets',
      value: stats?.openTickets ?? 0,
      icon: <Ticket className="h-4 w-4" />,
      description:
        stats?.openTickets === 1
          ? '1 ticket awaiting response'
          : `${stats?.openTickets ?? 0} tickets awaiting response`,
      variant: (stats?.openTickets ?? 0) > 0 ? ('warning' as const) : ('default' as const),
    },
    {
      title: 'Recent Invoices',
      value: stats?.recentInvoices ?? 0,
      icon: <FileText className="h-4 w-4" />,
      description: 'Invoices in the last 30 days',
      variant: 'default' as const,
    },
    {
      title: 'Account Status',
      value: accountStatusDisplay?.label ?? 'Unknown',
      icon:
        stats?.accountStatus?.toLowerCase() === 'active' ? (
          <CheckCircle className="h-4 w-4" />
        ) : (
          <AlertCircle className="h-4 w-4" />
        ),
      description: accountStatusDisplay?.description ?? '',
      variant: accountStatusDisplay?.variant ?? 'default',
    },
  ];

  // Add total spent card
  if (stats?.totalSpent !== undefined && stats.totalSpent !== null) {
    statCards.push({
      title: 'Total Spent',
      value: formatCurrency(stats.totalSpent),
      icon: <CreditCard className="h-4 w-4" />,
      description: 'Lifetime spending',
      variant: 'default' as const,
    });
  }

  // Add last login card
  if (stats?.lastLogin) {
    statCards.push({
      title: 'Last Login',
      value: formatRelativeDate(stats.lastLogin),
      icon: <Clock className="h-4 w-4" />,
      description: new Date(stats.lastLogin).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
      variant: 'default' as const,
    });
  }

  // Add pending payments card if there are any
  if (stats?.pendingPayments && stats.pendingPayments > 0) {
    statCards.push({
      title: 'Pending Payments',
      value: stats.pendingPayments,
      icon: <AlertCircle className="h-4 w-4" />,
      description: 'Payments awaiting processing',
      variant: 'warning' as const,
    });
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {statCards.map((card) => (
        <StatCard
          key={card.title}
          title={card.title}
          value={card.value}
          icon={card.icon}
          description={card.description}
          variant={card.variant}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
}

export default CustomerStats;
